// Wallet-free public chain read for a video — the entry point to Decision 1
// of docs/plans/2026-08-13-verification-integration-closeout.md: the API
// only points at where to look, the chain is the authority on what is true.
//
// Reads must work with no wallet and no account, so this derives PDAs from
// the AUTHORITY THE API RECORDED (Video.publication.chain.authority), never
// from a connected wallet — a public viewer has none, and even a logged-in
// admin's wallet need not be the video's publisher. It reads through
// useForumVideo (frontend/src/solana/forum/useForumVideo.ts), which is
// itself wallet-free: it only needs a connection, via useProgramReader, and
// never useProgram or a bare BorshAccountsCoder (see that hook's and
// useProgramReader's doc comments for why).
import { Buffer } from 'buffer'
import { useMemo } from 'react'
import { useForumVideo } from '../solana/forum/index.js'
import type { VideoAccount } from '../solana/forum/index.js'
import type { Video } from '../types.js'

export type AnchorState = 'unanchored' | 'published' | 'sealed'

export interface ChainRecord {
  rootHash: string // lowercase hex
  state: AnchorState
  revision: number
  publishedAt: Date
  sealedAt: Date | null // null when sealed by window expiry rather than explicitly
  windowExpires: Date
}

// Mirrors CORRECTION_WINDOW in chain/programs/forum/src/lib.rs and
// CorrectionWindow in api/pkg/data/publication.go. All three must stay in
// step — this is the browser's own copy of a rule the chain enforces, so a
// drift here would show a badge that disagrees with what the chain actually
// does. See CLAUDE.md's note on that program's constants being settled: do
// not change the chain to match this file, change this file to match it.
const CORRECTION_WINDOW_SECONDS = 7 * 24 * 60 * 60

const toHex = (bytes: number[] | Uint8Array): string => Buffer.from(bytes).toString('hex')

// Anchor decodes the Rust enum's variants as `{ published: {} }` / `{ sealed: {} }`.
// Same pattern as chainStateOf in ChainAnchorBlock.tsx — the program has
// exactly these two variants, so "not explicitly sealed" is always "published".
const isExplicitlySealed = (state: VideoAccount['state']): boolean => 'sealed' in (state as object)

/** The subset of a decoded Video account computeChainRecord actually reads.
 * Narrower than VideoAccount on purpose: a test fixture only has to supply
 * five fields, not fabricate every account field the program happens to
 * store. A real VideoAccount satisfies this structurally, no cast needed. */
export type ChainRecordSource = Pick<VideoAccount, 'rootHash' | 'revision' | 'publishedAt' | 'sealedAt' | 'state'>

/**
 * Computes a ChainRecord from a decoded on-chain Video account. Pure and
 * exported so the is_sealed rule — the part this ticket is TDD'd against —
 * is testable without a validator, a wallet, or a React render.
 *
 * Mirrors Video::is_sealed in chain/programs/forum/src/lib.rs exactly:
 *
 *   pub fn is_sealed(&self, now: i64) -> bool {
 *       self.state == VideoState::Sealed
 *           || now.saturating_sub(self.published_at) > CORRECTION_WINDOW
 *   }
 *
 * Boundary: strictly greater-than, matching the Rust program (and its Go
 * mirror, data.IsSealed) exactly — a video is still correctable at the
 * instant the window closes and seals only once that instant has passed.
 */
export function computeChainRecord(account: ChainRecordSource, nowSeconds: number): ChainRecord {
  const publishedAtSeconds = account.publishedAt.toNumber()
  const sealedAtSeconds = account.sealedAt.toNumber()
  const sealedByWindow = nowSeconds - publishedAtSeconds > CORRECTION_WINDOW_SECONDS
  const sealed = isExplicitlySealed(account.state) || sealedByWindow

  return {
    rootHash: toHex(account.rootHash),
    state: sealed ? 'sealed' : 'published',
    revision: account.revision,
    publishedAt: new Date(publishedAtSeconds * 1000),
    // Zero means "never explicitly sealed" — including when `sealed` above
    // is true purely because the window expired. Showing a timestamp here
    // would claim a seal moment that never happened; see hardening §3.
    sealedAt: sealedAtSeconds > 0 ? new Date(sealedAtSeconds * 1000) : null,
    windowExpires: new Date((publishedAtSeconds + CORRECTION_WINDOW_SECONDS) * 1000),
  }
}

export interface ChainReadState {
  chain: ChainRecord | null // null while loading, or when the video was never anchored
  loading: boolean
  error: Error | null
}

/**
 * Reads a video's chain record with no wallet and no account. This is what
 * lets `PublicVideo.tsx` and `EmbedVideo.tsx` show a verification badge to a
 * visitor who has never connected a wallet.
 */
export function useChainRecord(video: Video): ChainReadState {
  const authority = video.publication?.chain?.authority ?? null
  const { video: account, loading, error } = useForumVideo(video.id, authority)

  const chain = useMemo(() => (account ? computeChainRecord(account, Math.floor(Date.now() / 1000)) : null), [account])

  return { chain, loading, error }
}
